'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useAppName } from '@/contexts/AppNameContext';
import { useLangCurrency } from '@/contexts/LangCurrencyContext';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';
import { MENU_ITEMS } from './navItems';

interface SidebarProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({
  collapsed,
  onCollapsedChange,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const { lang } = useLangCurrency();
  const { appName } = useAppName();
  const [showPreferences, setShowPreferences] = useState(false);

  const handleOpenPreferences = () => {
    setShowPreferences(true);
  };

  const sidebarWidthClass = collapsed ? 'w-16' : 'w-56';

  return (
    <>
      {/* Mobile overlay — only on small screens */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={onMobileClose} />
      )}

      {/* Sidebar rail — fixed at every viewport: the overlay pattern below md,
          a permanent rail on md+. AppLayout's main carries the matching ml. */}
      <aside
        className={[
          'fixed left-0 top-0 bottom-0 z-40 bg-card border-r border-border transition-transform duration-300 ease-in-out overflow-y-auto flex flex-col',
          sidebarWidthClass,
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0',
        ].join(' ')}
        style={{
          maxHeight: '100dvh',
        }}
      >
        {/* Logo Section */}
        <div className="p-4 border-b border-border flex items-center justify-between min-h-[64px]">
          <div className="flex items-center gap-2 min-w-0">
            {!collapsed && (
              <span
                className="text-sm font-semibold text-foreground truncate"
                suppressHydrationWarning
              >
                {appName}
              </span>
            )}
          </div>
          <button
            onClick={() => onCollapsedChange(!collapsed)}
            className="p-1 rounded-lg hover:bg-surface transition-colors flex-shrink-0 hidden md:flex"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={16} />
          </button>
        </div>

        {/* Menu Items — scroll within the visible rail on mobile */}
        <nav className="p-3 space-y-1 pb-4 flex-1">
          {MENU_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-surface transition-all duration-150 group"
              title={collapsed ? (lang === 'EN' ? item.label : item.labelTh) : ''}
              onClick={onMobileClose}
            >
              <Icon name={item.icon as any} size={18} className="flex-shrink-0" />
              {!collapsed && (
                <span className="truncate">{lang === 'EN' ? item.label : item.labelTh}</span>
              )}
            </Link>
          ))}
        </nav>

        {/* Back to public site */}
        <div className={`border-t border-border ${collapsed ? 'p-2' : 'p-3'}`}>
          <Link
            href="/landing"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-surface transition-all duration-150"
            title={collapsed ? 'Back to Site' : ''}
            onClick={onMobileClose}
          >
            <Icon name="GlobeAltIcon" size={18} className="flex-shrink-0" />
            {!collapsed && <span>Back to Site</span>}
          </Link>
        </div>
      </aside>

      {/* Preference Center Modal */}
      {showPreferences && (
        <ConsentPreferenceCenter
          region="gdpr"
          onClose={() => setShowPreferences(false)}
          onSave={() => setShowPreferences(false)}
        />
      )}
    </>
  );
}
